import { useCallback } from 'react';
import cx from 'clsx';
import { useToast } from '@kcd/design-system';

import CartCard from '~/domains/cart/components/CartCard';
import {
  useDeleteShoppingCartItemByShoppingCartItemId,
  useToggleCartItemSelect,
  useUpdateCartItemFixQuantity,
} from '~/domains/cart/queries';
import { getOAIErrorMessage } from '~/libs/openapi';
import { OAIApiError } from '~/libs/openApiError';
import type { OAISchema } from '~/types/openapi';

import { CartVendorDeliveryInfo } from '../CartVendorDeliveryInfo';

type Props = {
  vendorId: OAISchema['MarketShoppingCartVendorGroupResponse']['vendorId'];
  shoppingCartItemBundles: OAISchema['MarketShoppingCartVendorGroupResponse']['shoppingCartItemBundles'];
};

type CartItem = OAISchema['MarketShoppingCartItemResponseV2'];

export const VendorBundles = ({ vendorId, shoppingCartItemBundles }: Props) => {
  const toast = useToast();
  const toggleItemMutate = useToggleCartItemSelect(vendorId);
  const quantityMutate = useUpdateCartItemFixQuantity(vendorId);
  const deleteCartItemMutate =
    useDeleteShoppingCartItemByShoppingCartItemId(vendorId);

  const toggleShoppingCartItem = useCallback(
    (item: CartItem, checked: boolean) => {
      toggleItemMutate(
        {
          requestBody: {
            isSelected: checked,
            shoppingCartItemId: item.shoppingCartItemId!,
            cartType: item.cartType,
          },
        },
        {
          onError: (err) => {
            const errMessage = getOAIErrorMessage(err);
            toast(errMessage, { offset: 100 });
          },
        },
      );
    },
    [toast, toggleItemMutate],
  );

  const onDeleteCartItem = useCallback(
    ({ shoppingCartItemId }: CartItem) => {
      if (!shoppingCartItemId) return;
      try {
        deleteCartItemMutate({
          shoppingCartItemId,
        });
      } catch (err) {
        if (err instanceof OAIApiError) {
          const errMessage = getOAIErrorMessage(err);
          toast(errMessage, { offset: 100 });
          return;
        }
        throw err;
      }
    },
    [deleteCartItemMutate, toast],
  );

  const onChangeCartItemQuantity = useCallback(
    (item: CartItem, quantity: number) => {
      try {
        void quantityMutate({
          requestBody: {
            offerId: item.offerId!,
            stockKeepingUnits: [
              {
                skuId: item.skuId!,
                quantity,
              },
            ],
          },
        });
      } catch (err) {
        if (err instanceof OAIApiError) {
          const errMessage = getOAIErrorMessage(err);
          toast(errMessage, { offset: 100 });
          return;
        }
        throw err;
      }
    },
    [quantityMutate, toast],
  );

  return (
    <>
      {shoppingCartItemBundles.map((bundle) => {
        if (!bundle.shoppingCartItems.length) return null;

        return (
          <div
            key={bundle.deliveryPolicyId}
            className="mb-16 overflow-hidden rounded-16 border border-solid border-gray-200"
          >
            {bundle.expectedDeliveryText && (
              <p
                className={cx([
                  'py-12 px-20 text-paragraph typo-paragraph100-semibold',
                  bundle.deliveryType === 'COURIER' && 'bg-[#fff5f0]',
                  bundle.deliveryType === 'SELF' && 'bg-blue-100',
                ])}
              >
                지금 결제 시 {bundle.expectedDeliveryText}
              </p>
            )}

            {bundle.shoppingCartItems.map((item) => (
              <CartCard
                item={item}
                key={item.offerId}
                onDeleteClick={() => onDeleteCartItem(item)}
                onChangeCheckbox={(changed) =>
                  toggleShoppingCartItem(item, changed)
                }
                onCountChange={(quantity) =>
                  onChangeCartItemQuantity(item, quantity)
                }
                className="px-20"
              />
            ))}

            <CartVendorDeliveryInfo
              paymentResponse={
                bundle.inShoppingCartDeliveryBundlePaymentResponse
              }
            />
          </div>
        );
      })}
    </>
  );
};
